import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Link2, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Xero Tenant Resolution (read-only). xero_tenant_to_entity_id used to
 * default an unrecognized tenant name to br_ltd silently -- fixed to
 * return unresolved instead. This card shows the live connected-tenant
 * mapping plus a historical audit of existing br_ltd Xero-source invoices
 * whose stored tenant name doesn't actually match a br_ltd key.
 */
export function XeroTenantResolutionPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.xeroTenantResolution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Xero Tenant Resolution
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Live resolution for every connected Xero tenant, plus a historical audit of existing
          br_ltd Xero-source invoices for signs of the old silent-default (unmapped tenant
          attributed to br_ltd). Read-only.
        </p>
        <Button size="sm" onClick={run} disabled={loading} data-testid="xero-tenant-resolution-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
        </Button>

        {result && (
          <div className="space-y-4 text-sm">
            <div>
              <h5 className="font-semibold text-xs mb-1">Connected tenants</h5>
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">tenant</th>
                      <th className="py-1 pr-2">entity_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.live_tenants.map((t, i) => (
                      <tr key={i} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono">{t.tenant_name}</td>
                        <td className="py-1 pr-2">
                          {t.resolved
                            ? <span className="font-mono">{t.entity_id}</span>
                            : <Badge variant="destructive" className="text-[10px]">UNRESOLVED</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={
              'rounded p-3 border flex items-start gap-2 ' +
              (result.historical_audit.mismatched_count > 0
                ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900'
                : 'bg-muted/50 border-transparent')
            }>
              {result.historical_audit.mismatched_count > 0 && (
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              )}
              <div className="text-xs">
                <div className="font-semibold">
                  Historical audit: {result.historical_audit.mismatched_count} / {result.historical_audit.scanned} possibly mis-attributed
                </div>
                {result.historical_audit.truncated && (
                  <div className="text-amber-600">Scan truncated at cap -- more may exist.</div>
                )}
                {result.historical_audit.sample.length > 0 && (
                  <div className="mt-2 overflow-x-auto max-h-40 overflow-y-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="py-0.5 pr-2">id</th>
                          <th className="py-0.5 pr-2">invoice_number</th>
                          <th className="py-0.5 pr-2">entity_name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.historical_audit.sample.map((d, i) => (
                          <tr key={i} className="border-b border-border/20">
                            <td className="py-0.5 pr-2 font-mono">{d.id}</td>
                            <td className="py-0.5 pr-2 font-mono">{d.invoice_number}</td>
                            <td className="py-0.5 pr-2 font-mono">{d.entity_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">{result.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
